/*
  Toolbar für die erste Shiftplan-V2-Ansicht.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
// Dasselbe Symbol wie in /settings fuer "Abteilungen & Schichten".
import IconDepartment from "@mui/icons-material/CorporateFareOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  IconArrowLeft,
  IconAvailability,
  IconChevronDown,
  IconSettings,
  IconWarning,
} from "../../../../design-system/icons";
import AppInput from "../../../../design-system/components/AppInput";
import AppPeriodNavigator from "../../../../design-system/components/AppPeriodNavigator";
import ShiftplanTooltip from "../tooltips/ShiftplanTooltip";

const RELEASE_ALL_KEY = "__all__";
const RELEASE_PREVIEW_SCOPES = Object.freeze({
  PERIOD: "period",
  ALL: "all",
});

function rowModeLabel(value) {
  return value === "department" ? "Abteilungen" : "Mitarbeitende";
}

function RowModeIcon({ value }) {
  return value === "department" ? <IconDepartment fontSize="small" /> : <PersonOutlineIcon fontSize="small" />;
}

function formatOpenShiftCount(count) {
  const normalizedCount = Number(count || 0);
  return normalizedCount === 1 ? "1 Schicht nicht freigegeben" : `${normalizedCount} Schichten nicht freigegeben`;
}

function formatReleaseShiftCount(count) {
  const normalizedCount = Number(count || 0);
  return normalizedCount === 1 ? "1 Schicht freigeben" : `${normalizedCount} Schichten freigeben`;
}

function buildReleaseScopeOptions(totalCount, departments) {
  return [
    { key: RELEASE_ALL_KEY, name: "Alle", count: Number(totalCount || 0) },
    ...(Array.isArray(departments) ? departments : []),
  ];
}

function ReleaseScopeRow({
  scope,
  icon,
  title,
  totalCount,
  departments,
  selectedKey,
  // Ohne folgenden Empfaengerschritt keine Auslassungspunkte: der Klick gibt
  // dann sofort frei.
  releaseLabel = "Freigeben…",
  onSelectedKeyChange,
  onRelease,
  onPreviewChange,
  disabled,
}) {
  const rowRef = useRef(null);
  const [isPreviewIntentActive, setIsPreviewIntentActive] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [hoverPreviewKey, setHoverPreviewKey] = useState("");
  const [pinnedPreviewKey, setPinnedPreviewKey] = useState("");
  const wasPreviewActiveRef = useRef(false);
  const options = useMemo(() => buildReleaseScopeOptions(totalCount, departments), [departments, totalCount]);
  const activeOption = options.find((option) => option.key === selectedKey) || options[0];
  const activeKey = activeOption?.key || RELEASE_ALL_KEY;
  const previewKey = isSelectOpen && hoverPreviewKey ? hoverPreviewKey : pinnedPreviewKey || activeKey;
  const previewOption = options.find((option) => option.key === previewKey) || activeOption;
  const canPreview = !disabled && Boolean(previewOption) && Number(previewOption.count || 0) > 0;
  const isPreviewActive = canPreview && (isPreviewIntentActive || isSelectOpen || Boolean(pinnedPreviewKey));
  const previewDepartmentKey = previewOption?.key === RELEASE_ALL_KEY ? "" : previewOption?.key || "";

  useEffect(() => {
    if (!isPreviewActive) {
      if (wasPreviewActiveRef.current) {
        onPreviewChange?.(null);
        wasPreviewActiveRef.current = false;
      }
      return undefined;
    }

    wasPreviewActiveRef.current = true;
    onPreviewChange?.({ scope, departmentKey: previewDepartmentKey });
  }, [isPreviewActive, onPreviewChange, previewDepartmentKey, scope]);

  useEffect(() => {
    return () => {
      if (wasPreviewActiveRef.current) {
        onPreviewChange?.(null);
        wasPreviewActiveRef.current = false;
      }
    };
  }, [onPreviewChange]);

  useEffect(() => {
    if (!pinnedPreviewKey) return undefined;

    const handleMouseMove = (event) => {
      const target = event.target;
      const isInsideRow = rowRef.current?.contains(target);
      const isInsideMenu =
        target && typeof target.closest === "function" && target.closest(".spv2-release-scope-menu");

      if (isInsideRow || isInsideMenu) return;

      setPinnedPreviewKey("");
      setHoverPreviewKey("");
      setIsPreviewIntentActive(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [pinnedPreviewKey]);

  useEffect(() => {
    if (!pinnedPreviewKey) return;
    if (options.some((option) => option.key === pinnedPreviewKey)) return;
    setPinnedPreviewKey("");
  }, [options, pinnedPreviewKey]);

  const handleSelectedKeyChange = (nextKey) => {
    const normalizedKey = nextKey || RELEASE_ALL_KEY;

    onSelectedKeyChange?.(normalizedKey);
    setPinnedPreviewKey(normalizedKey);
    setHoverPreviewKey(normalizedKey);
  };

  const handleOptionPreview = (nextKey) => {
    const normalizedKey = nextKey || RELEASE_ALL_KEY;

    setHoverPreviewKey(normalizedKey);
    if (pinnedPreviewKey && pinnedPreviewKey !== normalizedKey) {
      setPinnedPreviewKey("");
    }
  };

  const handleBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsPreviewIntentActive(false);
  };

  const handleRowPreviewEnter = () => {
    setIsPreviewIntentActive(true);

    if (isSelectOpen) return;

    setHoverPreviewKey("");
    if (activeKey !== RELEASE_ALL_KEY || pinnedPreviewKey) {
      setPinnedPreviewKey(activeKey);
    }
  };

  return (
    <Box
      ref={rowRef}
      className={`spv2-release-scope-row${isPreviewActive ? " is-previewing" : ""}`}
      onMouseEnter={handleRowPreviewEnter}
      onMouseLeave={() => setIsPreviewIntentActive(false)}
      onFocus={handleRowPreviewEnter}
      onBlur={handleBlur}
    >
      <span className="spv2-release-action-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="spv2-release-scope-copy">
        <span className="spv2-release-action-title">{title}</span>
      </span>
      <Select
        className="spv2-release-scope-select"
        size="small"
        value={activeKey}
        disabled={disabled || !options.length}
        IconComponent={IconChevronDown}
        onChange={(event) => handleSelectedKeyChange(event.target.value)}
        onOpen={() => {
          setIsSelectOpen(true);
          setHoverPreviewKey(activeKey);
        }}
        onClose={() => {
          setIsSelectOpen(false);
          setHoverPreviewKey("");
        }}
        inputProps={{ "aria-label": `${title}: Ziel` }}
        MenuProps={{
          PaperProps: { className: "spv2-release-scope-menu" },
          MenuListProps: {
            onMouseLeave: () => setHoverPreviewKey(""),
          },
        }}
        renderValue={(value) => {
          const option = options.find((item) => item.key === value) || activeOption;
          return option ? `${option.name} (${option.count})` : "Alle";
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.key}
            value={option.key}
            className={previewKey === option.key ? "is-preview-option" : ""}
            onMouseEnter={() => handleOptionPreview(option.key)}
            onFocus={() => handleOptionPreview(option.key)}
          >
            <span className="spv2-release-scope-option-name">{option.name}</span>
            <span className="spv2-release-scope-option-count">{option.count}</span>
          </MenuItem>
        ))}
      </Select>
      <Button
        type="button"
        className="spv2-release-scope-btn"
        disabled={disabled || !activeOption || activeOption.count <= 0}
        onClick={() => onRelease?.(activeKey === RELEASE_ALL_KEY ? "" : activeKey)}
      >
        {releaseLabel}
      </Button>
    </Box>
  );
}

function formatRecipientShiftCount(count) {
  const normalizedCount = Number(count || 0);
  return normalizedCount === 1 ? "1 Schicht" : `${normalizedCount} Schichten`;
}

function ReleaseNotifyStep({ pendingRelease, recipients, isSaving, onBack, onRelease, onPreviewChange }) {
  const reachableRecipients = useMemo(() => recipients.filter((recipient) => recipient.hasPhone), [recipients]);
  const [selectedIds, setSelectedIds] = useState(() => new Set(reachableRecipients.map((recipient) => recipient.id)));
  const scope = pendingRelease?.scope || RELEASE_PREVIEW_SCOPES.PERIOD;
  const departmentKey = pendingRelease?.departmentKey || "";

  // Die Ziel-Schichten bleiben während des Benachrichtigungs-Schritts im Raster markiert.
  useEffect(() => {
    onPreviewChange?.({ scope, departmentKey });
    return () => onPreviewChange?.(null);
  }, [departmentKey, onPreviewChange, scope]);

  const toggleRecipient = (recipient) => {
    if (!recipient.hasPhone) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(recipient.id)) {
        next.delete(recipient.id);
      } else {
        next.add(recipient.id);
      }
      return next;
    });
  };

  const selectedCount = selectedIds.size;
  const unreachableCount = recipients.length - reachableRecipients.length;
  const shiftCount = Number(pendingRelease?.shiftCount || 0);
  const releaseTitle = [pendingRelease?.scopeTitle, pendingRelease?.departmentName].filter(Boolean).join(" · ");

  return (
    <>
      <Box className="spv2-release-head">
        <IconButton className="spv2-release-back-btn" onClick={onBack} disabled={isSaving} aria-label="Zurück zur Auswahl">
          <IconArrowLeft fontSize="small" />
        </IconButton>
        <Box className="spv2-release-head-copy">
          <Typography className="spv2-release-title">{formatReleaseShiftCount(shiftCount)}</Typography>
          <Typography className="spv2-release-subtitle">{releaseTitle}</Typography>
        </Box>
      </Box>

      <Box className="spv2-release-notify-info">
        <span className="spv2-release-notify-info-icon" aria-hidden="true">
          <WhatsAppIcon fontSize="small" />
        </span>
        <span className="spv2-release-notify-info-text">
          Ausgewählte Mitarbeitende werden per <strong>WhatsApp</strong> über den neuen Schichtplan benachrichtigt.
        </span>
      </Box>

      <Box className="spv2-release-notify-list" role="group" aria-label="Zu benachrichtigende Mitarbeitende">
        {recipients.map((recipient) => {
          const isSelected = selectedIds.has(recipient.id);
          return (
            <label
              key={recipient.id}
              className={`spv2-release-notify-row${recipient.hasPhone ? "" : " is-unreachable"}${
                isSelected ? " is-selected" : ""
              }`}
            >
              <Checkbox
                size="small"
                className="spv2-release-notify-checkbox"
                checked={isSelected}
                disabled={isSaving || !recipient.hasPhone}
                onChange={() => toggleRecipient(recipient)}
                inputProps={{ "aria-label": `${recipient.name} benachrichtigen` }}
              />
              <span className="spv2-release-notify-name">{recipient.name}</span>
              <span className="spv2-release-notify-meta">
                {recipient.hasPhone ? formatRecipientShiftCount(recipient.shiftCount) : "Keine Telefonnummer"}
              </span>
            </label>
          );
        })}
      </Box>

      {unreachableCount > 0 ? (
        <Typography className="spv2-release-notify-hint">
          {unreachableCount === 1
            ? "1 Person ist ohne Telefonnummer nicht per WhatsApp erreichbar."
            : `${unreachableCount} Personen sind ohne Telefonnummer nicht per WhatsApp erreichbar.`}
        </Typography>
      ) : null}

      <Box className="spv2-release-notify-actions">
        <Button
          type="button"
          className="spv2-release-notify-secondary-btn"
          disabled={isSaving}
          onClick={() => onRelease([])}
        >
          Nur freigeben
        </Button>
        <Button
          type="button"
          className="spv2-release-notify-primary-btn"
          disabled={isSaving || selectedCount === 0}
          startIcon={<WhatsAppIcon fontSize="small" />}
          onClick={() => onRelease(Array.from(selectedIds))}
        >
          {`Freigeben & benachrichtigen (${selectedCount})`}
        </Button>
      </Box>
    </>
  );
}

function ReleaseDraftsPopover({
  open,
  anchorEl,
  summary,
  isSaving,
  canNotify = false,
  buildReleaseRecipients,
  onClose,
  onReleaseAll,
  onReleasePeriod,
  onPreviewChange,
}) {
  const [periodScopeKey, setPeriodScopeKey] = useState(RELEASE_ALL_KEY);
  const [allScopeKey, setAllScopeKey] = useState(RELEASE_ALL_KEY);
  const [pendingRelease, setPendingRelease] = useState(null);
  const totalCount = Number(summary?.totalCount || 0);
  const periodCount = Number(summary?.periodCount || 0);
  const allDepartments = Array.isArray(summary?.allDepartments) ? summary.allDepartments : [];
  const periodDepartments = Array.isArray(summary?.periodDepartments) ? summary.periodDepartments : [];
  const periodLabel = summary?.periodLabel || "aktueller Zeitraum";
  const hasOpenShifts = totalCount > 0;

  useEffect(() => {
    if (!open) setPendingRelease(null);
  }, [open]);

  const releaseScope = (scope, departmentKey, notifyUserIds) => {
    if (scope === RELEASE_PREVIEW_SCOPES.PERIOD) {
      onReleasePeriod?.(departmentKey, notifyUserIds);
    } else {
      onReleaseAll?.(departmentKey, notifyUserIds);
    }
  };

  /*
    Mit gebuchtem WhatsApp folgt der Schritt zur Empfängerauswahl. Ohne das
    Zusatzmodul gäbe es dort nichts zu wählen — dann wird direkt freigegeben.
  */
  const startRelease = (scope, departmentKey, scopeTitle, departments, scopeCount) => {
    if (!canNotify) {
      onClose?.();
      releaseScope(scope, departmentKey, []);
      return;
    }

    const department = departments.find((item) => item.key === departmentKey);
    setPendingRelease({
      scope,
      departmentKey,
      scopeTitle,
      departmentName: department?.name || "Alle Abteilungen",
      shiftCount: departmentKey ? Number(department?.count || 0) : Number(scopeCount || 0),
    });
  };

  const handleRelease = (notifyUserIds) => {
    const release = pendingRelease;
    setPendingRelease(null);
    onClose?.();
    if (!release) return;

    releaseScope(release.scope, release.departmentKey, notifyUserIds);
  };

  const releaseLabel = canNotify ? "Freigeben…" : "Freigeben";

  const notifyRecipients = useMemo(
    () =>
      pendingRelease
        ? buildReleaseRecipients?.(pendingRelease.scope, pendingRelease.departmentKey) || []
        : [],
    [buildReleaseRecipients, pendingRelease]
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ className: "spv2-release-popover-paper" }}
    >
      <Box className="spv2-release-popover">
        {pendingRelease ? (
          <ReleaseNotifyStep
            // Remount bei Scope-Wechsel, damit die Empfänger-Auswahl frisch startet.
            key={`${pendingRelease.scope}:${pendingRelease.departmentKey}`}
            pendingRelease={pendingRelease}
            recipients={notifyRecipients}
            isSaving={isSaving}
            onBack={() => setPendingRelease(null)}
            onRelease={handleRelease}
            onPreviewChange={onPreviewChange}
          />
        ) : (
          <>
            <Box className="spv2-release-head">
              <Box className="spv2-release-head-icon" aria-hidden="true">
                <PendingActionsOutlinedIcon fontSize="small" />
              </Box>
              <Box className="spv2-release-head-copy">
                <Typography className="spv2-release-title">Freigabe</Typography>
                <Typography className="spv2-release-subtitle">
                  {hasOpenShifts ? formatOpenShiftCount(totalCount) : "Alle Schichten sind freigegeben"}
                </Typography>
              </Box>
            </Box>

            <Box className="spv2-release-actions">
              <ReleaseScopeRow
                scope={RELEASE_PREVIEW_SCOPES.PERIOD}
                icon={<CalendarMonthOutlinedIcon fontSize="small" />}
                title={periodLabel}
                totalCount={periodCount}
                departments={periodDepartments}
                selectedKey={periodScopeKey}
                releaseLabel={releaseLabel}
                disabled={isSaving || periodCount === 0}
                onSelectedKeyChange={setPeriodScopeKey}
                onRelease={(scopeKey) =>
                  startRelease(RELEASE_PREVIEW_SCOPES.PERIOD, scopeKey, periodLabel, periodDepartments, periodCount)
                }
                onPreviewChange={onPreviewChange}
              />
              <ReleaseScopeRow
                scope={RELEASE_PREVIEW_SCOPES.ALL}
                icon={<DoneAllOutlinedIcon fontSize="small" />}
                title="Alle offenen"
                totalCount={totalCount}
                departments={allDepartments}
                selectedKey={allScopeKey}
                releaseLabel={releaseLabel}
                disabled={isSaving || totalCount === 0}
                onSelectedKeyChange={setAllScopeKey}
                onRelease={(scopeKey) =>
                  startRelease(RELEASE_PREVIEW_SCOPES.ALL, scopeKey, "Alle offenen", allDepartments, totalCount)
                }
                onPreviewChange={onPreviewChange}
              />
            </Box>
          </>
        )}
      </Box>
    </Popover>
  );
}

/*
  Zeitraum, Monat/Woche und Zeilen (Mitarbeitende/Abteilungen) - der Teil der
  Werkzeugleiste, den auch die Mitarbeitenden-Ansicht hat. Alles, was den Plan
  veraendert, bleibt in ShiftplanV2Toolbar.
*/
export function ShiftplanPeriodControls({
  viewMode,
  rowMode,
  referenceDate,
  periodLabel,
  onViewModeChange,
  onRowModeChange,
  onPreviousPeriod,
  onNextPeriod,
  onPeriodSelect,
}) {
  const isWeekView = viewMode === "week";
  const previousTooltip = isWeekView ? "Vorherige Woche" : "Vorheriger Monat";
  const nextTooltip = isWeekView ? "Nächste Woche" : "Nächster Monat";

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} className="spv2-toolbar-period">
      {/* Dieselbe Navigation wie auf /availability und /demand. */}
      <AppPeriodNavigator
        viewMode={viewMode}
        referenceDate={referenceDate}
        periodLabel={periodLabel}
        previousLabel={previousTooltip}
        nextLabel={nextTooltip}
        onPreviousPeriod={onPreviousPeriod}
        onNextPeriod={onNextPeriod}
        onSelectDate={onPeriodSelect}
        onViewModeChange={onViewModeChange}
      />

      <Box className="spv2-toolbar-mode-controls">
        <Select
          className="spv2-toolbar-mode-select spv2-toolbar-mode-select--rows"
          size="small"
          value={rowMode}
          onChange={(event) => onRowModeChange(event.target.value)}
          IconComponent={IconChevronDown}
          renderValue={(value) => (
            <span className="spv2-toolbar-row-mode-value" aria-hidden="true">
              <RowModeIcon value={value} />
            </span>
          )}
          inputProps={{ "aria-label": `Rasterzeilen: ${rowModeLabel(rowMode)}` }}
          MenuProps={{ classes: { paper: "spv2-toolbar-mode-menu spv2-toolbar-row-mode-menu" } }}
        >
          <MenuItem value="employee" aria-label="Mitarbeitende">
            <span className="spv2-toolbar-row-mode-option">
              <span className="spv2-toolbar-row-mode-menu-icon">
                <RowModeIcon value="employee" />
              </span>
              <span>Mitarbeitende</span>
            </span>
          </MenuItem>
          <MenuItem value="department" aria-label="Abteilungen">
            <span className="spv2-toolbar-row-mode-option">
              <span className="spv2-toolbar-row-mode-menu-icon">
                <RowModeIcon value="department" />
              </span>
              <span>Abteilungen</span>
            </span>
          </MenuItem>
        </Select>
      </Box>
    </Stack>
  );
}

export default function ShiftplanV2Toolbar({
  viewMode,
  rowMode,
  referenceDate,
  periodLabel,
  searchQuery,
  onViewModeChange,
  onRowModeChange,
  onSearchQueryChange,
  onPreviousPeriod,
  onNextPeriod,
  onPeriodSelect,
  onOpenSettings,
  onOpenPeriodCopy,
  onOpenExport,
  onOpenSolver,
  canUseSolver = false,
  isSolverOpen = false,
  isSolverRunning = false,
  solverBadgeCount = 0,
  isSettingsOpen = false,
  isPeriodCopyOpen = false,
  isExportOpen = false,
  releaseSummary,
  isReleaseSaving = false,
  // Zusatzmodul WhatsApp: nur damit folgt der Empfaengerschritt.
  canNotifyOnRelease = false,
  buildReleaseRecipients,
  onReleaseAllDrafts,
  onReleasePeriodDrafts,
  onReleasePreviewChange,
  conflictModel,
  isConflictsOpen = false,
  onToggleConflicts,
  showAvailability = false,
  onShowAvailabilityChange,
}) {
  const [releaseAnchor, setReleaseAnchor] = useState(null);
  const isReleaseOpen = Boolean(releaseAnchor);
  const releaseCount = Number(releaseSummary?.totalCount || 0);
  const conflictCount = Number(conflictModel?.totalCount || 0);

  const handleOpenSettings = () => {
    onOpenSettings?.("order");
  };

  const handleReleaseOpen = (event) => {
    setReleaseAnchor(event.currentTarget);
  };

  const handleReleaseClose = () => {
    setReleaseAnchor(null);
    onReleasePreviewChange?.(null);
  };

  const availabilityTooltip = showAvailability ? "Verfügbarkeiten ausblenden" : "Verfügbarkeiten anzeigen";
  const releaseTooltip = releaseCount ? formatOpenShiftCount(releaseCount) : "Keine offenen Schichten";
  const conflictsTooltip = conflictCount
    ? conflictCount === 1
      ? "1 Konflikt im Zeitraum"
      : `${conflictCount} Konflikte im Zeitraum`
    : "Keine Konflikte im Zeitraum";
  const settingsButtonClassName = `spv2-toolbar-icon-btn${isSettingsOpen ? " is-open" : ""}`;
  const periodCopyButtonClassName = `spv2-toolbar-icon-btn${isPeriodCopyOpen ? " is-open" : ""}`;
  const exportButtonClassName = `spv2-toolbar-icon-btn${isExportOpen ? " is-open" : ""}`;

  return (
    <div className="spv2-toolbar-wrap">
      <div className="spv2-toolbar-row">
        <ShiftplanPeriodControls
          viewMode={viewMode}
          rowMode={rowMode}
          referenceDate={referenceDate}
          periodLabel={periodLabel}
          onViewModeChange={onViewModeChange}
          onRowModeChange={onRowModeChange}
          onPreviousPeriod={onPreviousPeriod}
          onNextPeriod={onNextPeriod}
          onPeriodSelect={onPeriodSelect}
        />

        <div className="spv2-toolbar-right">
          <div className="spv2-toolbar-search">
            <AppInput
              type="search"
              size="sm"
              clearable
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
              placeholder="Mitarbeitende suchen..."
              aria-label="Mitarbeitende suchen"
            />
          </div>
          <ShiftplanTooltip title={releaseTooltip}>
            <Button
              className={`spv2-toolbar-release-btn${releaseCount ? " has-open" : " is-clear"}${
                isReleaseOpen ? " is-open" : ""
              }`}
              aria-label={releaseTooltip}
              aria-haspopup="dialog"
              aria-expanded={isReleaseOpen}
              onClick={handleReleaseOpen}
              startIcon={<PendingActionsOutlinedIcon fontSize="small" />}
            >
              <span>{releaseCount} offen</span>
            </Button>
          </ShiftplanTooltip>
          <ShiftplanTooltip title={conflictsTooltip}>
            <IconButton
              className={`spv2-toolbar-icon-btn spv2-toolbar-conflicts-btn${isConflictsOpen ? " is-open" : ""}`}
              aria-label={conflictsTooltip}
              aria-expanded={isConflictsOpen}
              onClick={() => onToggleConflicts?.()}
            >
              <IconWarning fontSize="small" />
              {conflictCount ? (
                <span className="spv2-toolbar-conflicts-badge" aria-hidden="true">
                  {conflictCount > 99 ? "99+" : conflictCount}
                </span>
              ) : null}
            </IconButton>
          </ShiftplanTooltip>
          <ShiftplanTooltip title={availabilityTooltip}>
            <IconButton
              className={`spv2-toolbar-icon-btn spv2-toolbar-availability-btn${showAvailability ? " is-active" : ""}`}
              aria-label={availabilityTooltip}
              aria-pressed={showAvailability}
              onClick={() => onShowAvailabilityChange?.(!showAvailability)}
            >
              <IconAvailability fontSize="small" />
            </IconButton>
          </ShiftplanTooltip>
          {canUseSolver ? (
            <ShiftplanTooltip
              title={
                isSolverRunning
                  ? "Automatisch planen — ein Lauf rechnet gerade"
                  : solverBadgeCount
                    ? solverBadgeCount === 1
                      ? "Automatisch planen — 1 neuer Plan wartet"
                      : `Automatisch planen — ${solverBadgeCount} neue Pläne warten`
                    : "Automatisch planen (Solver)"
              }
            >
              <IconButton
                className={`spv2-toolbar-icon-btn spv2-toolbar-solver-btn${isSolverOpen ? " is-open" : ""}${
                  isSolverRunning ? " is-computing" : ""
                }`}
                onClick={() => onOpenSolver?.()}
                aria-label="Automatisch planen"
                aria-pressed={isSolverOpen}
                aria-haspopup="dialog"
              >
                <AutoFixHighOutlinedIcon fontSize="small" />
                {/* Rechen-Ring, solange ein Lauf queued/running ist */}
                {isSolverRunning ? <span className="spv2-toolbar-solver-ring" aria-hidden="true" /> : null}
                {/* Badge = fertige Läufe, die auf Übernehmen/Verwerfen warten */}
                {solverBadgeCount ? (
                  <span className="spv2-toolbar-conflicts-badge spv2-toolbar-solver-badge" aria-hidden="true">
                    {solverBadgeCount > 99 ? "99+" : solverBadgeCount}
                  </span>
                ) : null}
              </IconButton>
            </ShiftplanTooltip>
          ) : null}
          {/* Trennt das Arbeiten am Plan (Konflikte, Verfügbarkeit, Solver) von
              dem, was mit dem fertigen Plan passiert (kopieren, exportieren). */}
          <span className="spv2-toolbar-divider" aria-hidden="true" />
          <ShiftplanTooltip title="Zeitraum kopieren">
            <IconButton
              className={periodCopyButtonClassName}
              onClick={() => onOpenPeriodCopy?.()}
              aria-label="Zeitraum kopieren"
              aria-pressed={isPeriodCopyOpen}
              aria-haspopup="dialog"
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </ShiftplanTooltip>
          <ShiftplanTooltip title="Exportieren">
            <IconButton
              className={exportButtonClassName}
              aria-label="Exportieren"
              aria-haspopup="dialog"
              aria-expanded={isExportOpen}
              onClick={() => onOpenExport?.()}
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </ShiftplanTooltip>
          <ShiftplanTooltip title="Einstellungen">
            <IconButton
              className={settingsButtonClassName}
              onClick={handleOpenSettings}
              aria-label="Einstellungen"
              aria-pressed={isSettingsOpen}
              aria-haspopup="dialog"
            >
              <IconSettings fontSize="small" />
            </IconButton>
          </ShiftplanTooltip>
        </div>
      </div>

      <ReleaseDraftsPopover
        open={isReleaseOpen}
        anchorEl={releaseAnchor}
        summary={releaseSummary}
        isSaving={isReleaseSaving}
        canNotify={canNotifyOnRelease}
        buildReleaseRecipients={buildReleaseRecipients}
        onClose={handleReleaseClose}
        onReleaseAll={onReleaseAllDrafts}
        onReleasePeriod={onReleasePeriodDrafts}
        onPreviewChange={onReleasePreviewChange}
      />
    </div>
  );
}
